import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { blockMember } from "@/lib/blocking";

const REASONS = [
  { key: "spam", label: "Spam or scam" },
  { key: "harassment", label: "Harassment or bullying" },
  { key: "sexual_minor", label: "Sexualized content involving a minor" },
  { key: "nonconsensual", label: "Non-consensual content" },
  { key: "hate", label: "Hate speech" },
  { key: "self_harm", label: "Self-harm or violence" },
  { key: "other", label: "Something else" },
];

export default function ReportModal({ open, onOpenChange, target, onBlocked }) {
  const [reasonKey, setReasonKey] = useState("");
  const [detail, setDetail] = useState("");
  const [alsoBlock, setAlsoBlock] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!reasonKey || busy) return;
    setBusy(true);
    const label = REASONS.find((r) => r.key === reasonKey)?.label || reasonKey;
    const reason = detail.trim() ? `${label}: ${detail.trim()}` : label;
    try {
      const { data } = await api.post("/reports", {
        target_type: target.type,
        target_id: target.id,
        reason,
      });
      if (target?.type === "user" && alsoBlock) {
        try {
          await blockMember(target.id, { onBlocked });
        } catch {
          toast.error("Reported, but block failed — try from their profile.");
        }
      } else if (data.duplicate) {
        toast("Thanks — this was already reported.", { icon: "🐼" });
      } else {
        toast.success("Reported. Our team will review shortly.");
      }
      setReasonKey("");
      setDetail("");
      setAlsoBlock(false);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send report.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass rounded-none max-w-md border-white/10" data-testid="report-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Report {target?.type === "live_chat" ? "message" : target?.type}</DialogTitle>
          <DialogDescription className="text-zinc-500 text-sm">
            Reports are private — the {target?.type === "user" ? "user" : "author"} won't know.
          </DialogDescription>
        </DialogHeader>
        <div className="text-xs text-zinc-300 border-l-2 border-rose-600 pl-3 leading-relaxed" data-testid="report-sla-notice">
          Our moderation team reviews every report <strong className="text-white">within 24 hours</strong>. Content that breaks our rules is removed and the person responsible is warned, suspended or permanently ejected. Explicit sexual acts, harassment and hate are never allowed.
        </div>
        <div className="grid grid-cols-1 gap-2">
          {REASONS.map((r) => (
            <button
              key={r.key}
              type="button"
              data-testid={`report-reason-${r.key}`}
              onClick={() => setReasonKey(r.key)}
              className={`text-left px-4 py-3 border-l-2 text-sm transition-colors ${
                reasonKey === r.key
                  ? "border-rose-600 bg-white/[0.03] text-white"
                  : "border-transparent text-zinc-400 hover:text-white hover:bg-white/[0.02]"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
        {reasonKey && (
          <Textarea
            data-testid="report-detail-input"
            value={detail}
            onChange={(e) => setDetail(e.target.value)}
            placeholder="Add any context (optional)…"
            className="bg-black/40 border-white/10 rounded-none focus-visible:ring-rose-600"
          />
        )}
        {target?.type === "user" && (
          <label className="flex items-center gap-2 text-sm text-zinc-400 cursor-pointer select-none" data-testid="report-also-block">
            <input
              type="checkbox"
              checked={alsoBlock}
              onChange={(e) => setAlsoBlock(e.target.checked)}
              className="accent-rose-600"
            />
            Also block this user
          </label>
        )}
        <div className="flex justify-end">
          <Button
            data-testid="report-submit-button"
            onClick={submit}
            disabled={!reasonKey || busy}
            className="btn-crimson rounded-none px-6 py-5"
          >
            {busy ? "Sending…" : "Send report"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
